import { useState } from "react";
import { PageHeader } from "../components/layout/PageHeader";
import { HOOKS, SETTINGS, VIDEO_FORMATS, RECENT_VIDEOS } from "../lib/sample-data";
import {
  Megaphone, Wand2, Link as LinkIcon, Upload, Sparkles, UserSquare2, Camera, ShoppingBag,
} from "lucide-react";
import { cn } from "../lib/utils";

const UGC_STYLES = [
  "iPhone selfie", "Studio shot", "Outdoor handheld", "Vlog cut", "Hot take", "Walk and talk",
];

export default function MarketingStudio() {
  const [tab, setTab] = useState("url");
  const [url, setUrl] = useState("");
  const [hook, setHook] = useState(HOOKS[0]);
  const [setting, setSetting] = useState(SETTINGS[0]);
  const [ugc, setUgc] = useState(UGC_STYLES[0]);

  return (
    <div data-testid="marketing-studio-page" className="space-y-8">
      <PageHeader
        kicker="Marketing Studio"
        title="Turn products into ad creatives"
        description="From a URL, a product image, or a reference ad — generate Hyper Motion, Unboxing, UGC, Demo, Testimonial, and Story ads."
        actions={
          <button
            data-testid="ms-generate-btn"
            className="inline-flex items-center gap-2 px-4 h-11 rounded-xl pl-btn-aqua text-sm"
          >
            <Wand2 className="h-4 w-4" /> Generate Ad
          </button>
        }
      />

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl border border-[var(--pl-border)] bg-[var(--pl-surface)]/60 p-5 space-y-4">
          <div className="flex flex-wrap gap-1.5" data-testid="ms-source-tabs">
            <SourceTab id="url" active={tab} onClick={setTab} icon={LinkIcon}>URL to Ad</SourceTab>
            <SourceTab id="product" active={tab} onClick={setTab} icon={ShoppingBag}>Product Upload</SourceTab>
            <SourceTab id="ref" active={tab} onClick={setTab} icon={Camera}>Ad Reference</SourceTab>
          </div>

          {tab === "url" && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">Product / Page URL</div>
              <input
                data-testid="ms-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://your-product.com/offer"
                style={{ background: "color-mix(in srgb, var(--pl-surface) 70%, transparent)", color: "var(--pl-text)" }}
                className="mt-2 w-full rounded-xl border border-[var(--pl-border)] px-3 h-11 text-sm focus:outline-none focus:border-[var(--pl-aqua)]/50"
              />
              <div className="text-[11px] text-[var(--pl-text-muted)] mt-2">
                We scrape the page, extract claims, image, and CTA — then draft 5+ ad variants.
              </div>
            </div>
          )}
          {tab === "product" && (
            <UploadCard testId="ms-product-upload" title="Upload product photo" desc="JPG, PNG up to 25 MB. Or drop a folder of variants." />
          )}
          {tab === "ref" && (
            <UploadCard testId="ms-ref-upload" title="Upload ad reference" desc="We will study the structure, hook, and pacing." />
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <SelectField label="UGC Style" testId="ms-ugc" value={ugc} onChange={setUgc} options={UGC_STYLES} />
            <SelectField label="Hook" testId="ms-hook" value={hook} onChange={setHook} options={HOOKS} />
            <SelectField label="Setting" testId="ms-setting" value={setting} onChange={setSetting} options={SETTINGS} />
          </div>

          <div className="rounded-xl border border-[var(--pl-aqua)]/30 bg-[var(--pl-aqua)]/5 p-4 flex items-start gap-3">
            <UserSquare2 className="h-5 w-5 text-[var(--pl-aqua)] mt-0.5" />
            <div className="text-sm">
              <div className="text-[var(--pl-text)]">Use an AI Influencer as your talent</div>
              <div className="text-xs text-[var(--pl-text-muted)] mt-0.5">
                Aurora · Luxury · ready · or create one in <span className="text-[var(--pl-aqua)]">/ai-influencer</span>.
              </div>
            </div>
            <button
              data-testid="ms-pick-avatar"
              className="ml-auto h-9 px-3 rounded-xl border border-[var(--pl-border)] text-xs hover:border-[var(--pl-aqua)]/40 text-[var(--pl-text)]"
            >
              Pick avatar
            </button>
          </div>
        </div>

        <div className="rounded-2xl p-5 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">Generate across formats</div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            {VIDEO_FORMATS.map((f) => (
              <button
                key={f.id}
                data-testid={`ms-format-${f.id}`}
                className="rounded-xl px-3 py-3 text-xs border border-[var(--pl-border)] bg-white/[0.03] text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40 flex flex-col items-start gap-1"
              >
                <Megaphone className="h-3.5 w-3.5 text-[var(--pl-aqua)]" />
                {f.label}
              </button>
            ))}
          </div>
          <div className="mt-4 text-[11px] text-[var(--pl-text-muted)] flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5 text-[var(--pl-aqua)]" />
            Routes via Higgsfield + HeyGen when connected.
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">Recent Campaign Assets</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {RECENT_VIDEOS.map((v) => (
            <div
              key={v.id}
              data-testid={`ms-asset-${v.id}`}
              className="rounded-2xl overflow-hidden border border-[var(--pl-border)] bg-[var(--pl-surface)]/70 pl-lift"
            >
              <div className="aspect-[9/16] bg-[var(--pl-bg-2)] overflow-hidden">
                <img src={v.thumb} alt={v.title} className="w-full h-full object-cover" />
              </div>
              <div className="p-3">
                <div className="text-sm font-medium text-[var(--pl-text)] truncate">{v.title}</div>
                <div className="text-[11px] text-[var(--pl-text-muted)] font-mono mt-0.5">{v.meta}</div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function SourceTab({ id, active, onClick, icon: I, children }) {
  return (
    <button
      data-testid={`ms-tab-${id}`}
      onClick={() => onClick(id)}
      className={cn(
        "px-3 h-10 rounded-xl text-xs inline-flex items-center gap-2 border transition",
        active === id
          ? "bg-[var(--pl-aqua)]/15 border-[var(--pl-aqua)]/40 text-[var(--pl-aqua)]"
          : "border-[var(--pl-border)] text-[var(--pl-text-muted)] hover:text-[var(--pl-text)]"
      )}
    >
      <I className="h-3.5 w-3.5" />
      {children}
    </button>
  );
}

function SelectField({ label, testId, value, onChange, options }) {
  return (
    <label className="block space-y-1.5">
      <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--pl-text-muted)]">{label}</div>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ background: "color-mix(in srgb, var(--pl-surface) 70%, transparent)", color: "var(--pl-text)" }}
        className="w-full rounded-xl border border-[var(--pl-border)] px-3 h-10 text-sm focus:outline-none focus:border-[var(--pl-aqua)]/50"
      >
        {options.map((o) => <option key={o}>{o}</option>)}
      </select>
    </label>
  );
}

function UploadCard({ title, desc, testId }) {
  return (
    <button
      data-testid={testId}
      className="w-full rounded-xl border border-dashed border-[var(--pl-border)] p-6 flex items-center gap-3 hover:border-[var(--pl-aqua)]/50 transition text-left"
    >
      <div className="h-12 w-12 rounded-xl bg-[var(--pl-aqua)]/10 border border-[var(--pl-aqua)]/30 grid place-items-center">
        <Upload className="h-5 w-5 text-[var(--pl-aqua)]" />
      </div>
      <div>
        <div className="text-sm font-medium text-[var(--pl-text)]">{title}</div>
        <div className="text-[11px] text-[var(--pl-text-muted)]">{desc}</div>
      </div>
    </button>
  );
}
